import { makeGraphqlRequestCloud } from 'api/graphql/graphQLClient';
import { MeasurementShort } from '@/graphql/cloud/measurements/queries/MeasurementShort.graphql';
import { MeasurementShortByUlid } from '@/graphql/cloud/measurements/queries/MeasurementShortByUlid.graphql';
import { MeasurementsBySequenceId } from '@/graphql/cloud/measurements/queries/MeasurementsBySequenceId.graphql';
import { MeasurementsShort } from '@/graphql/cloud/measurements/queries/MeasurementsShort.graphql';
import { ResetMeasurementPostprocessing } from '@/graphql/cloud/measurements/mutations/ResetMeasurementPostprocessing.graphql';
import { RequestRecalculationMeasurementPostprocessing } from '@/graphql/cloud/measurements/mutations/RequestRecalculationMeasurementPostprocessing.graphql';
import { ApplyMeasurementPostprocessing } from '@/graphql/cloud/measurements/mutations/ApplyMeasurementPostprocessing.graphql';
import { MeasurementPostprocessings as SubscriptionForMeasurementPostprocessings } from '@/graphql/cloud/measurements/subscriptions/MeasurementPostprocessings.graphql';
import { MeasurementsWithUlid } from '@/graphql/cloud/measurements/queries/MeasurementsWithUlid.graphql';
import { Measurement } from '@/graphql/cloud/measurements/queries/Measurement.graphql';
import { MeasurementForCompare } from '@/graphql/cloud/measurements/queries/MeasurementForCompare.graphql';
import { MeasurementsData } from '@/graphql/cloud/measurements/queries/MeasurementsData.graphql';
import { Measurement as SubscriptionForMeasurement } from '@/graphql/cloud/measurements/subscriptions/Measurement.graphql';
import { PostprocessedMeasurementsBySequenceId } from '@/graphql/cloud/measurements/queries/PostprocessedMeasurementsBySequenceIdCount.graphql';
import { TotalMeasurementsBySequenceId } from '@/graphql/cloud/measurements/queries/TotalMeasurementsBySequenceIdCount.graphql';
import { parseMeasurementsWithPostprocessing } from '@/utils/postprocessingHelpers';
import { getDataMeasurementFromCDN } from '@/utils/deviceDataConvertors';
import { sleep } from '@/utils/timeoutHelpers';
import { subscribe } from '@/api/graphql/wsHasuraClient';
import { mapPeaksWithEstimation } from '@/utils/estimationHelpers';

import { CreateManualPeak } from '@/graphql/cloud/measurements/mutations/CreateManualPeak.graphql';
import { DeletePeak } from '@/graphql/cloud/measurements/mutations/DeletePeak.graphql';
import { DeletePeaks } from '@/graphql/cloud/measurements/mutations/DeletePeaks.graphql';
import { createGraphqlPollingSubscription } from '@/utils/subscriptionHelpers';

export const apiMeasurements = {
  async getMeasurementShort(measurementId) {
    const { measurement } = await makeGraphqlRequestCloud(MeasurementShort, {
      id: measurementId,
    });

    if (!measurement) {
      throw new Error('Measurement is not found!');
    }

    return measurement;
  },

  async getMeasurementShortByUlid(ulid) {
    const response = await makeGraphqlRequestCloud(MeasurementShortByUlid, {
      ulid,
    });
    const measurement = response.measurement?.[0];

    if (!measurement) {
      throw new Error('Measurement is not found!');
    }

    return measurement;
  },

  getMeasurementsBySequenceId(sequenceId) {
    return makeGraphqlRequestCloud(MeasurementsBySequenceId, {
      sequenceId,
    });
  },

  async getMeasurementsShort(injectionId) {
    const { measurements } = await makeGraphqlRequestCloud(MeasurementsShort, {
      injectionId,
    });

    if (!measurements) {
      throw new Error('Measurements are not found!');
    }

    return { measurements };
  },
  async getMeasurementsWithUlid(measurementIds) {
    const { measurements } = await makeGraphqlRequestCloud(MeasurementsWithUlid, {
      measurementIds,
    });

    if (!measurements) {
      throw new Error('Measurements are not found!');
    }

    return measurements;
  },
  resetPostprocessing(measurementId) {
    return makeGraphqlRequestCloud(ResetMeasurementPostprocessing, {
      measurementId,
    });
  },
  requestRecalculationMeasurementPostprocessing(measurementId) {
    return makeGraphqlRequestCloud(RequestRecalculationMeasurementPostprocessing, {
      measurementId,
    });
  },
  applyPostprocessing(postprocessing) {
    return makeGraphqlRequestCloud(ApplyMeasurementPostprocessing, {
      postprocessing,
    });
  },
  async getMeasurement(measurementId, sampleId, shareToken) {
    const { measurement } = await makeGraphqlRequestCloud(
      Measurement,
      {
        measurementId,
      },
      1,
      shareToken,
      sampleId,
    );

    return {
      ...measurement,
      peaks: mapPeaksWithEstimation(measurement.peaks),
      // stub to avoid some errors
      data: [],
      async getData(isApplyPostprocessing) {
        const url = isApplyPostprocessing ? measurement.processed_data_url : measurement.data_url;

        if (url) {
          try {
            const data = await getDataMeasurementFromCDN(url);
            return data;
          } catch (e) {
            return [];
          }
        }
        return [];
      },
      async getBaseline() {
        if (measurement.baseline_url) {
          try {
            const baseline = await getDataMeasurementFromCDN(measurement.baseline_url);
            return baseline;
          } catch (e) {
            return [];
          }
        }
        return [];
      },
    };
  },
  async getMeasurementForCompare(measurementId, sampleId, shareToken) {
    const { measurement } = await makeGraphqlRequestCloud(
      MeasurementForCompare,
      {
        measurementId,
      },
      1,
      shareToken,
      sampleId,
    );

    // Handle the edge case when a postprocessing was applied but there are no computations
    const hasPostprocessing = measurement?.postprocessings?.length > 0;
    const wasComputed = measurement.processed_data_url?.length > 0;
    if (hasPostprocessing && !wasComputed) {
      await sleep(1000);
      return this.getMeasurementForCompare(measurementId, sampleId, shareToken);
    }

    const detectionTime = measurement?.postprocessings.find(
      (postprocessing) => postprocessing.name === 'DTIM',
    );

    return {
      ...measurement,
      injection: {
        ...measurement?.injection,
        type: measurement?.injection.standard_id ? 'standard' : 'sample',
      },
      detectionTime: {
        start_sec: detectionTime?.params?.detection_time_start_sec,
        end_sec: detectionTime?.params?.detection_time_end_sec,
      },
      data: await getDataMeasurementFromCDN(
        measurement?.postprocessings.length > 0
          ? measurement.processed_data_url
          : measurement.data_url,
      ),
    };
  },
  subscribeForMeasurementsData(ulid, onData, shareToken, sharedObjectId, settings = {}) {
    let lastProcessedFrom = -1;
    return createGraphqlPollingSubscription({
      initialPaused: settings.isPaused ?? false,
      shouldSkip: () => !ulid,
      fetcher: {
        document: MeasurementsData,
        getVariables: () => ({ ulid, from: lastProcessedFrom }),
        shareToken,
        sharedObjectId,
      },
      onData({ measurementsData }) {
        lastProcessedFrom = Math.max(
          lastProcessedFrom,
          ...(Array.isArray(measurementsData) && measurementsData.length > 0
            ? measurementsData.map(({ from }) => from)
            : [lastProcessedFrom]),
        );
        onData(measurementsData ?? []);
      },
      onError(error) {
        console.error('Error fetching measurements data:', error);
      },
    });
  },
  subscribeForPostprocessings(injectionId, onPostprocessings, shareToken, sharedObjectId) {
    return subscribe(
      SubscriptionForMeasurementPostprocessings,
      { injectionId },
      ({ measurementsWithPostprocessing }) => {
        const postprocessings = parseMeasurementsWithPostprocessing(measurementsWithPostprocessing);

        onPostprocessings(postprocessings?.length > 0 ? postprocessings : null);
      },
      shareToken,
      sharedObjectId,
    );
  },
  subscribeForMeasurement(measurementId, onMeasurement, shareToken, sharedObjectId) {
    return subscribe(
      SubscriptionForMeasurement,
      { measurementId },
      ({ measurement }) => {
        onMeasurement({
          ...measurement,
          peaks: mapPeaksWithEstimation(measurement.peaks),
          data: [],
          hasBinaryData: measurement.data_url != null,
          async getData(isApplyPostprocessing) {
            const url = isApplyPostprocessing
              ? measurement.processed_data_url
              : measurement.data_url;
            console.warn('getData url', url, url);
            if (url) {
              try {
                return await getDataMeasurementFromCDN(url);
              } catch (e) {
                console.error(e);
                return [];
              }
            }
            return [];
          },
          async getBaseline() {
            if (measurement.baseline_url) {
              try {
                return await getDataMeasurementFromCDN(measurement.baseline_url);
              } catch (e) {
                return [];
              }
            }
            return [];
          },
        });
      },
      shareToken,
      sharedObjectId,
    );
  },

  subscribeForPostprocessedMeasurement(sequenceId, onProgress = {}) {
    return createGraphqlPollingSubscription({
      intervalMs: 2000,
      shouldSkip: () => !sequenceId,
      fetcher: {
        document: PostprocessedMeasurementsBySequenceId,
        getVariables: () => ({ sequenceId }),
      },
      onData({ postprocessedMeasurements }) {
        const count = postprocessedMeasurements?.aggregate?.count ?? 0;
        onProgress({ postprocessedMeasurements: count });
      },
      onError(error) {
        console.error('Error fetching postprocessed measurements count:', error);
      },
    });
  },
  async getTotalMeasurementsCountBySequenceId(sequenceId) {
    const { totalMeasurements } = await makeGraphqlRequestCloud(TotalMeasurementsBySequenceId, {
      sequenceId,
    });

    return totalMeasurements?.aggregate?.count ?? 0;
  },
  async createManualPeak(data) {
    const { createManualPeak } = await makeGraphqlRequestCloud(CreateManualPeak, {
      data,
    });
    if (!createManualPeak.success) {
      throw new Error(createManualPeak.message || 'Failed to create manual peak');
    }
    return createManualPeak;
  },
  async deletePeak(peakId) {
    const { deletePeak } = await makeGraphqlRequestCloud(DeletePeak, { peakId });
    if (!deletePeak.success) {
      throw new Error(deletePeak.message || 'Failed to delete peak');
    }
    return deletePeak;
  },
  async deletePeaks(peakIds) {
    const { deletePeaks } = await makeGraphqlRequestCloud(DeletePeaks, { peakIds });
    if (!deletePeaks.success) {
      throw new Error(deletePeaks.message || 'Failed to delete peaks');
    }
    return deletePeaks;
  },
};
